import React, { useMemo, useState, useEffect } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import * as XLSX from "xlsx";
import Master from "../../student/Master";
import {
  studentImportEnrollBatchV1,
  studentImportEnrollBatchV2,
} from "../../../api/StudentImport";
import { batchList } from "../../../api/Batch";

const CONDITIONAL_FIELDS = [
  {
    key: "registration_fee_status",
    value: "Registration Fee Status",
    feeType: "Registration Fee",
  },
  {
    key: "transport_status",
    value: "Transport Status",
    feeType: "Institute Transport Fee",
  },
  {
    key: "hostel_status",
    value: "Hostel Status",
    feeType: "Institute Hostel Fee",
  },
  {
    key: "library_status",
    value: "Library Status",
    feeType: "Institute Library Fee",
  },
];

const BASE_FIELDS = [
  { key: "registration_no", value: "Registration No" },
  { key: "rte_status", value: "RTE Status" },
];

const fieldAliases = {
  registration_no: [
    "registration no",
    "sr no",
    "admission no",
    "student reg no",
  ],
  rte_status: ["rte", "rte_status", "getting free education", "free"],
  registration_fee_status: ["registration fee status", "reg fee status"],
  transport_status: ["transport status", "transport"],
  hostel_status: ["hostel status", "hostel"],
  library_status: ["library status", "library"],
};

const normalize = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export default function StudentsBatchEnroll() {
  const [loading, setLoading] = useState(false);

  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelSample, setExcelSample] = useState({});
  const [excelData, setExcelData] = useState([]);
  const [importStatus, setImportStatus] = useState({});
  const [progress, setProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [editableData, setEditableData] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [ImportType, setImportType] = useState("new");
  const [mappingRows, setMappingRows] = useState([
    { field: "registration_no", excel_column: "" },
  ]);

  useEffect(() => {
    setSessions(JSON.parse(localStorage.getItem("sessions") ?? "[]"));
  }, []);

  const getBatchList = async (session_id = "") => {
    setLoading(true);
    try {
      let addUrl = "limit=all";
      if (session_id) {
        addUrl += `&session_id=${session_id}`;
      }
      const response = await batchList(addUrl);
      if (response?.data?.status) {
        setBatches(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message || "Failed");
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBatchList();
  }, []);

  const selectedBatch = useMemo(
    () => batches.find((b) => String(b.id) === String(batchId)),
    [batches, batchId],
  );

  const batchFeeTypeSet = useMemo(() => {
    const set = new Set();
    (selectedBatch?.fee_details || []).forEach((fd) => {
      if (fd?.status === "1" && fd?.fee_type) set.add(fd.fee_type);
    });
    return set;
  }, [selectedBatch]);

  const studentFields = useMemo(() => {
    const conditional = CONDITIONAL_FIELDS.filter((f) =>
      batchFeeTypeSet.has(f.feeType),
    );
    return [...BASE_FIELDS, ...conditional];
  }, [batchFeeTypeSet]);

  useEffect(() => {
    setMappingRows((prev) => {
      const validKeys = studentFields.map((f) => f.key);
      const filtered = prev.filter(
        (r) => !r.field || validKeys.includes(r.field),
      );
      return filtered.length ? filtered : [{ field: "", excel_column: "" }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentFields]);

  const numericFields = [];
  const dateFields = [];

  const downloadBlankTemplate = () => {
    if (!batchId) {
      return SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch first, the template columns depend on the batch's fee structure",
      );
    }

    const headers = studentFields.map((f) => `${f.value.toUpperCase()} *`);

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "student_import_template.xlsx");
  };

  // ---------------------------------------------------------------------
  // File upload / auto mapping
  // ---------------------------------------------------------------------
  const handleFileUpload = (e) => {
    if (!batchId) {
      e.target.value = "";
      return SweetAlertShow(
        "info",
        "Select Batch",
        "Please select a batch before uploading the sheet",
      );
    }

    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      SweetAlertShow("error", "Invalid File", "Please upload Excel file");
      return;
    }

    const reader = new FileReader();

    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        dateNF: "dd-mm-yyyy",
      });

      if (!jsonData.length) return;

      const headers = (jsonData[0] || [])
        .map((h) => String(h || "").trim())
        .filter(Boolean);

      const rows = jsonData
        .slice(1)
        .filter((row) =>
          row.some(
            (cell) =>
              cell !== undefined && cell !== null && String(cell).trim() !== "",
          ),
        );

      const secondRow = rows[0] || [];

      setExcelHeaders(headers);
      setExcelData(rows);
      setImportStatus({});
      setProgress(0);

      const sampleObj = {};
      headers.forEach((h, i) => {
        sampleObj[h] = secondRow[i] || "";
      });
      setExcelSample(sampleObj);

      const autoMap = studentFields
        .map((f) => {
          const possibleNames = [f.value, ...(fieldAliases[f.key] || [])];
          const match = headers.find((h) => {
            const hNorm = normalize(h);
            return possibleNames.some((name) => hNorm === normalize(name));
          });
          if (!match) return null;
          return { field: f.key, excel_column: match };
        })
        .filter(Boolean);

      setMappingRows(
        autoMap.length ? autoMap : [{ field: "", excel_column: "" }],
      );
    };

    reader.readAsArrayBuffer(file);
  };

  const handleAddRow = () => {
    setMappingRows([...mappingRows, { field: "", excel_column: "" }]);
  };

  const handleRemoveRow = (index) => {
    setMappingRows(mappingRows.filter((_, i) => i !== index));
  };

  const handleChange = (index, key, value) => {
    setMappingRows((prev) => {
      let updated = prev.map((row, i) =>
        i === index ? { ...row, [key]: value } : row,
      );

      if (key === "excel_column") {
        updated = updated.map((row, i) =>
          i !== index && row.excel_column === value
            ? { ...row, excel_column: "" }
            : row,
        );
      }
      if (key === "field") {
        updated = updated.map((row, i) =>
          i !== index && row.field === value ? { ...row, field: "" } : row,
        );
      }
      return [...updated];
    });
  };

  const usedFields = mappingRows.map((r) => r.field);

  // ---------------------------------------------------------------------
  // Mapping -> editable rows
  // ---------------------------------------------------------------------
  const getMappedData = () => {
    if (!excelData.length) return [];

    return excelData.map((row) => {
      const obj = {};

      mappingRows.forEach((map) => {
        if (!map.field || !map.excel_column) return;
        const colIndex = excelHeaders.findIndex((h) => h === map.excel_column);
        let value = colIndex !== -1 ? (row[colIndex] ?? "") : "";

        if (dateFields.includes(map.field)) {
          if (typeof value === "number") {
            const excelDate = XLSX.SSF.parse_date_code(value);
            value = `${excelDate.y}-${String(excelDate.m).padStart(2, "0")}-${String(
              excelDate.d,
            ).padStart(2, "0")}`;
          } else if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = `${date.getFullYear()}-${String(
                date.getMonth() + 1,
              ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            }
          }
        }

        if (numericFields.includes(map.field)) {
          value =
            value === "" ? "" : Number(String(value).replace(/,/g, "")) || 0;
        }

        if (map.field === "rte_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true", "rte"].includes(val)
            ? "RTE"
            : "NON-RTE";
        }

        if (map.field === "registration_fee_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true"].includes(val) ? "Yes" : "No";
        }
        if (map.field === "transport_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true"].includes(val) ? "Yes" : "No";
        }
        if (map.field === "hostel_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true"].includes(val) ? "Yes" : "No";
        }
        if (map.field === "library_status") {
          const val = String(value).toLowerCase().trim();
          value = ["yes", "y", "1", "true"].includes(val) ? "Yes" : "No";
        }

        obj[map.field] = value;
      });

      return obj;
    });
  };

  const mappedData = useMemo(
    () => getMappedData(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mappingRows, excelData, excelHeaders],
  );

  useEffect(() => {
    setEditableData(mappedData || []);
  }, [mappedData]);

  const handleCellChange = (rowIndex, field, value) => {
    const updated = [...editableData];
    updated[rowIndex][field] = value;
    setEditableData(updated);
  };

  const activeMappings = useMemo(
    () => mappingRows.filter((m) => m.field && m.excel_column),
    [mappingRows],
  );

  // ---------------------------------------------------------------------
  // Validation - every currently applicable field (base + batch-driven
  // fee status columns) is required
  // ---------------------------------------------------------------------
  const validateRow = (row) => {
    const errors = [];
    studentFields.forEach((f) => {
      if (row[f.key] === undefined || String(row[f.key]).trim() === "") {
        errors.push(`${f.value} required`);
      }
    });
    return errors;
  };

  const validationErrors = useMemo(
    () => editableData.map((row) => validateRow(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editableData, studentFields],
  );

  // ---------------------------------------------------------------------
  // Import
  // ---------------------------------------------------------------------
  const runImport = async (rowsToImport) => {
    let statusObj = { ...importStatus };
    setLoading(true);
    const chunkSize = 40;

    for (let i = 0; i < rowsToImport.length; i += chunkSize) {
      const chunk = rowsToImport.slice(i, i + chunkSize);
      // await Promise.allSettled(
      //   chunk.map(async (row) => {
      //     try {

      //          const res = await studentImportEnrollBatchV1({
      //           ...row.data,
      //           batch_id: batchId,
      //         });
      //       statusObj[row.index] = {
      //         status: res?.data?.status ? "success" : "error",
      //         message: res?.data?.message || "Failed",
      //       };
      //     } catch (err) {
      //       statusObj[row.index] = {
      //         status: "error",
      //         message:
      //           err?.response?.data?.message || err?.message || "Import Failed",
      //       };
      //     }
      //   }),
      // );

      await Promise.allSettled(
        chunk.map(async (row) => {
          try {
            let res;

            if (ImportType === "update") {
              res = await studentImportEnrollBatchV2({
                ...row.data,
                batch_id: batchId,
              });
            } else {
              res = await studentImportEnrollBatchV1({
                ...row.data,
                batch_id: batchId,
              });
            }

            statusObj[row.index] = {
              status: res?.data?.status ? "success" : "error",
              message: res?.data?.message || "Failed",
            };
          } catch (err) {
            statusObj[row.index] = {
              status: "error",
              message:
                err?.response?.data?.message || err?.message || "Import Failed",
            };
          }
        }),
      );

      setImportStatus({ ...statusObj });
      const processed = Object.keys(statusObj).length;
      setProgress(Math.round((processed / rowsToImport.length) * 100));
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    setLoading(false);
    return statusObj;
  };

  const handleStartImport = async () => {
    if (isImporting) return;

    if (!batchId) {
      return SweetAlertShow(
        "error",
        "Batch Required",
        "Please select a batch to enroll students into",
      );
    }

    const requiredFields = studentFields.map((f) => f.key);
    const missingFields = requiredFields.filter(
      (rf) => !mappingRows.some((m) => m.field === rf),
    );
    if (missingFields.length) {
      const labels = missingFields.map(
        (k) => studentFields.find((f) => f.key === k)?.value || k,
      );
      return SweetAlertShow(
        "error",
        "Missing Mapping",
        `Please map: ${labels.join(", ")}`,
      );
    }

    const rows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status !== "success",
      );

    if (!rows.length) {
      return SweetAlertShow(
        "error",
        "No Valid Rows",
        "Please fix validation errors",
      );
    }

    setProgress(0);
    setIsImporting(true);

    const finalStatus = await runImport(rows);

    const successCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "success",
    ).length;
    const failedCount = Object.values(finalStatus || {}).filter(
      (s) => s.status === "error",
    ).length;

    SweetAlertShow(
      "success",
      "Import Completed",
      `Success: ${successCount}, Failed: ${failedCount}`,
    );
    setIsImporting(false);
  };

  const handleRetryFailed = async () => {
    const failedRows = editableData
      .map((d, i) => ({ index: i, data: d, errors: validationErrors[i] }))
      .filter(
        (row) =>
          row.errors.length === 0 &&
          importStatus[row.index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    setIsImporting(true);
    await runImport(failedRows);
    setIsImporting(false);
  };

  const handleRetrySingle = async (index) => {
    if (validationErrors[index]?.length) {
      return SweetAlertShow(
        "error",
        "Validation Error",
        validationErrors[index].join(", "),
      );
    }
    setIsImporting(true);
    await runImport([{ index, data: editableData[index] }]);
    setIsImporting(false);
  };

  const downloadFailedRows = () => {
    const failedRows = editableData
      .map((row, index) => ({
        ...row,
        errors: validationErrors[index].join(", "),
        import_error: importStatus[index]?.message || "",
      }))
      .filter(
        (_, index) =>
          validationErrors[index]?.length ||
          importStatus[index]?.status === "error",
      );

    if (!failedRows.length) {
      return SweetAlertShow("info", "Info", "No failed rows");
    }

    const worksheet = XLSX.utils.json_to_sheet(failedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Rows");
    XLSX.writeFile(workbook, "failed_import_rows.xlsx");
  };

  const successCount = Object.values(importStatus).filter(
    (s) => s.status === "success",
  ).length;
  const failedCount = Object.values(importStatus).filter(
    (s) => s.status === "error",
  ).length;
  const validationCount = validationErrors.filter((e) => e.length).length;

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h5>Student Batch Enroll</h5>
                        <div className="row g-4 align-items-center">
                          <div className="col-sm-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="session_id"
                                onChange={(e) => {
                                  getBatchList(e.target.value);
                                }}
                              >
                                {sessions?.map((session) => (
                                  <option
                                    key={session?.id}
                                    value={session?.id}
                                    selected={session?.status === "1"}
                                  >
                                    {session?.session} :{" "}
                                    {session?.status === "1"
                                      ? "Active"
                                      : " Inactive"}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="course">Select session</label>
                            </div>
                          </div>

                          <div className="col-sm-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                              >
                                <option value="">Select Batch</option>
                                {batches?.map((batch) => (
                                  <option key={batch.id} value={batch.id}>
                                    {batch?.title} ({batch?.course?.title})
                                  </option>
                                ))}
                              </select>
                              <label className="ms-5">
                                Batch to enroll into
                              </label>
                            </div>
                          </div>

                          <div className="col-sm-4">
                            <button
                              className="btn btn-info"
                              onClick={downloadBlankTemplate}
                              disabled={!batchId}
                            >
                              Download Template
                            </button>
                          </div>

                          <div className="col-sm-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="file"
                                className="form-control"
                                onChange={handleFileUpload}
                                disabled={!batchId}
                              />
                              <label className="ms-5">Upload Excel</label>
                            </div>
                          </div>
                        </div>

                        {batchId && (
                          <div className="row mt-3">
                            <div className="col-12">
                              <small className="text-muted">
                                Required columns for this batch:{" "}
                                {studentFields.map((f) => f.value).join(", ")}
                              </small>
                            </div>
                          </div>
                        )}

                        <div className="row g-4 mt-2">
                          <div className="col-12">
                            <div className="d-flex justify-content-end mb-2">
                              <button
                                className="btn btn-outline-primary"
                                onClick={handleAddRow}
                                disabled={!excelHeaders.length}
                              >
                                Add Field
                              </button>
                            </div>

                            <div className="table-responsive">
                              <table className="table table-bordered">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th>Excel Column</th>
                                    <th>Preview</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {mappingRows?.map((row, index) => (
                                    <tr key={index}>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.field}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "field",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">Select Field</option>
                                          {studentFields?.map((f, i) => (
                                            <option
                                              key={i}
                                              value={f.key}
                                              disabled={
                                                usedFields.includes(f.key) &&
                                                row.field !== f.key
                                              }
                                            >
                                              {f.value}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <select
                                          className="form-select p-1"
                                          value={row.excel_column}
                                          onChange={(e) =>
                                            handleChange(
                                              index,
                                              "excel_column",
                                              e.target.value,
                                            )
                                          }
                                        >
                                          <option value="">
                                            Select Column
                                          </option>
                                          {excelHeaders.map((col, i) => (
                                            <option key={i} value={col}>
                                              {col}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="p-1">
                                        <input
                                          type="text"
                                          className="form-control p-1"
                                          readOnly
                                          value={
                                            excelSample[row.excel_column] || ""
                                          }
                                        />
                                      </td>
                                      <td className="p-1">
                                        <i
                                          className="ri-delete-bin-line text-danger"
                                          onClick={() => handleRemoveRow(index)}
                                        ></i>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mt-3">
                      <div className="col-md-4">
                        <div className="card bg-success text-white">
                          <div className="card-body">
                            Success : {successCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-danger text-white">
                          <div className="card-body">
                            Failed : {failedCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-warning">
                          <div className="card-body">
                            Validation Errors : {validationCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card mt-4">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5>Excel Preview</h5>
                          <div className="d-flex gap-2">
                            {/* <div className="d-flex align-items-center gap-2">
                              <select
                                className="form-select"
                                value={ImportType}
                                onChange={(e) => setImportType(e.target.value)}
                                disabled={isImporting}
                                style={{ width: "180px" }}
                              >
                                <option value="new">New Import</option>
                                <option value="update">Update Existing</option>
                              </select>

                              <button
                                className="btn btn-success"
                                onClick={handleStartImport}
                                disabled={isImporting || !editableData.length}
                              >
                                {isImporting ? "Importing..." : "Start Import"}
                              </button>
                            </div> */}
                            <button
                              className="btn btn-success"
                              onClick={handleStartImport}
                              disabled={isImporting || !editableData.length}
                            >
                              Start Import
                            </button>
                            <button
                              className="btn btn-warning"
                              onClick={handleRetryFailed}
                              disabled={isImporting}
                            >
                              Retry Failed
                            </button>
                            <button
                              className="btn btn-danger"
                              onClick={downloadFailedRows}
                            >
                              Download Failed
                            </button>
                          </div>
                        </div>

                        <div className="progress mb-3">
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                          >
                            {progress}%
                          </div>
                        </div>

                        <div className="table-responsive">
                          <table className="table table-bordered table-hover table-sm">
                            <thead className="table-dark">
                              <tr>
                                <th>SN</th>
                                {activeMappings?.map((m, i) => (
                                  <th key={i}>
                                    {m.field} ({m.excel_column})
                                  </th>
                                ))}
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {editableData?.map((row, i) => (
                                <tr
                                  key={i}
                                  className={`${
                                    importStatus[i]?.status === "error"
                                      ? "table-danger"
                                      : validationErrors[i]?.length
                                        ? "table-warning"
                                        : ""
                                  }`}
                                >
                                  <td>{i + 1}</td>
                                  {activeMappings.map((m, j) => (
                                    <td key={j} className="p-1">
                                      <input
                                        type="text"
                                        className="form-control p-1"
                                        value={row[m.field] ?? ""}
                                        onChange={(e) =>
                                          handleCellChange(
                                            i,
                                            m.field,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                  <td>
                                    {importStatus[i]?.status === "success" && (
                                      <span className="badge bg-success">
                                        Imported
                                      </span>
                                    )}
                                    {importStatus[i]?.status === "error" && (
                                      <>
                                        <span className="badge bg-danger">
                                          Failed
                                        </span>
                                        <button
                                          className="btn btn-warning btn-sm ms-2"
                                          onClick={() => handleRetrySingle(i)}
                                        >
                                          Retry
                                        </button>
                                        <div
                                          className="text-danger mt-1"
                                          style={{ fontSize: "11px" }}
                                        >
                                          {importStatus[i]?.message}
                                        </div>
                                      </>
                                    )}
                                    {!importStatus[i] && (
                                      <span className="badge bg-secondary">
                                        Pending
                                      </span>
                                    )}
                                    {validationErrors[i]?.length > 0 && (
                                      <div
                                        className="text-danger mt-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        {validationErrors[i].join(", ")}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
